import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/**
 * Reusable Modal component using Material UI's Dialog
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls whether the modal is visible
 * @param {function} props.onClose - Function to call when the modal should close
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {boolean} props.closeOnBackdropClick - Whether clicking the backdrop should close the modal
 * @param {React.ReactNode} props.actions - Optional footer actions (buttons)
 * @returns {JSX.Element}
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md', 
  closeOnBackdropClick = true,
  actions = null
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Map size prop to Material UI maxWidth
  const sizeMap = {
    xs: 'xs',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
    full: false // full width
  };
  
  const maxWidth = sizeMap[size] || 'md';
  
  // Handle modal close with options for backdrop click
  const handleClose = (event, reason) => {
    // Only close if closeOnBackdropClick is true or if it's not a backdrop click
    if (closeOnBackdropClick || reason !== 'backdropClick') {
      onClose(event, reason);
    }
  };
  
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen && size === 'full'}
      aria-labelledby="modal-title"
      PaperProps={{
        elevation: 5,
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
      // Fix scroll behavior without passive event listener issues
      disableScrollLock={true}
    >
      {/* Header */}
      <DialogTitle 
        id="modal-title"
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          edge="end"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      {/* Content */}
      <DialogContent dividers sx={{ p: 3 }}>
        {children}
      </DialogContent>
      
      {/* Actions (if provided) */}
      {actions && (
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
